import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, ExternalLink, Bell, BellOff, Radio } from "lucide-react";

/**
 * AdminSbtCompetitors — SBT Research Competitor Watch dashboard.
 * Two panels:
 *  1. Live Change Feed (default) — LLM-summarised diffs per competitor
 *     with dismissible cards + "Draft SBT response" tucked into each.
 *  2. Competitor list — CRUD + manual crawl.
 */
const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

function fmt(ts) {
  if (!ts) return "never";
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}
function kindLabel(k) {
  const map = {
    baseline_captured: { label: "Baseline", color: "bg-slate-200 text-slate-700" },
    title_change:      { label: "Title change", color: "bg-purple-100 text-purple-700" },
    headline_change:   { label: "H1 change", color: "bg-cyan-100 text-cyan-700" },
    meta_change:       { label: "Meta change", color: "bg-blue-100 text-blue-700" },
    new_blog_post:     { label: "New blog post", color: "bg-emerald-100 text-emerald-700" },
    content_change:    { label: "Content change", color: "bg-amber-100 text-amber-700" },
  };
  return map[k] || { label: k, color: "bg-slate-100 text-slate-600" };
}

export default function AdminSbtCompetitors() {
  const [competitors, setCompetitors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [filterCid, setFilterCid] = useState("");

  const token = () => localStorage.getItem("token");
  const hdr = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

  const load = async () => {
    setLoading(true);
    try {
      const [cr, er] = await Promise.all([
        fetch(`${API}/api/admin/sbt/competitors`, { headers: hdr() }),
        fetch(`${API}/api/admin/sbt/competitor-events?include_dismissed=${includeDismissed}${filterCid ? `&competitor_id=${filterCid}` : ""}`, { headers: hdr() }),
      ]);
      const cj = await cr.json();
      const ej = await er.json();
      setCompetitors(cj.competitors || []);
      setEvents(ej.events || []);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [includeDismissed, filterCid]);

  const crawlAll = async () => {
    setCrawling(true);
    try {
      const r = await fetch(`${API}/api/admin/sbt/competitors/crawl-all`, { method: "POST", headers: hdr() });
      const j = await r.json();
      toast.success(`Crawled — ${j.changes || 0} change(s), ${j.errors || 0} error(s)`);
      await load();
    } catch (e) { toast.error("Crawl failed"); }
    finally { setCrawling(false); }
  };
  const crawlOne = async (cid) => {
    try {
      const r = await fetch(`${API}/api/admin/sbt/competitors/${cid}/crawl`, { method: "POST", headers: hdr() });
      const j = await r.json();
      toast.success(j.changed ? "Change detected" : "No change since last crawl");
      await load();
    } catch { toast.error("Crawl failed"); }
  };
  const dismiss = async (eid) => {
    await fetch(`${API}/api/admin/sbt/competitor-events/${eid}/dismiss`, { method: "POST", headers: hdr() });
    setEvents(events.filter((e) => e.id !== eid || includeDismissed));
    if (includeDismissed) await load();
  };
  const removeCompetitor = async (cid) => {
    if (!window.confirm("Delete competitor and its change history?")) return;
    await fetch(`${API}/api/admin/sbt/competitors/${cid}`, { method: "DELETE", headers: hdr() });
    await load();
  };
  const toggleActive = async (c) => {
    await fetch(`${API}/api/admin/sbt/competitors/${c.id}`, { method: "PUT", headers: hdr(), body: JSON.stringify({ active: !c.active }) });
    await load();
  };

  if (loading) return <div className="py-10 text-center text-slate-500">Loading competitor watch…</div>;

  return (
    <div className="space-y-6" data-testid="admin-sbt-competitors">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Radio className="w-5 h-5 text-cyan-600" /> Competitor Watch
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {competitors.length} competitor{competitors.length === 1 ? "" : "s"} monitored daily. Claude Sonnet diffs each homepage &amp; blog and drafts an SBT response for every material change.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="competitors-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={crawlAll} disabled={crawling} data-testid="competitors-crawl-all">
            <RefreshCw className={`w-4 h-4 mr-1 ${crawling ? "animate-spin" : ""}`} /> {crawling ? "Crawling…" : "Crawl now"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="feed">
        <TabsList>
          <TabsTrigger value="feed" data-testid="comp-tab-feed">Change Feed ({events.filter((e) => !e.dismissed).length})</TabsTrigger>
          <TabsTrigger value="list" data-testid="comp-tab-list">Monitored Competitors ({competitors.length})</TabsTrigger>
          <TabsTrigger value="add"  data-testid="comp-tab-add">Add competitor</TabsTrigger>
        </TabsList>

        {/* ── CHANGE FEED ── */}
        <TabsContent value="feed" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={filterCid}
              onChange={(e) => setFilterCid(e.target.value)}
              className="text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white"
              data-testid="comp-filter"
            >
              <option value="">All competitors</option>
              {competitors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="text-xs text-slate-600 flex items-center gap-2 cursor-pointer">
              <Switch checked={includeDismissed} onCheckedChange={setIncludeDismissed} data-testid="comp-include-dismissed" />
              Include dismissed
            </label>
          </div>
          {events.length === 0 && (
            <div className="text-xs text-slate-400 italic border border-dashed rounded-lg p-10 text-center">
              No changes captured yet. Click <span className="font-semibold">Crawl now</span> to seed the baseline.
            </div>
          )}
          {events.map((e) => {
            const k = kindLabel(e.kind);
            return (
              <Card key={e.id} className={e.dismissed ? "opacity-60" : ""} data-testid={`event-${e.id}`}>
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{e.competitor_name}</span>
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${k.color}`}>{k.label}</span>
                      <span className="text-xs text-slate-400">· {fmt(e.captured_at)}</span>
                    </div>
                    {!e.dismissed && (
                      <button onClick={() => dismiss(e.id)} className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1" data-testid={`dismiss-${e.id}`}>
                        <BellOff className="w-3.5 h-3.5" /> Dismiss
                      </button>
                    )}
                  </div>

                  {e.llm_summary && (
                    <div className="bg-slate-50 rounded-md p-3">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Summary</div>
                      <div className="text-sm text-slate-800 leading-relaxed">{e.llm_summary}</div>
                    </div>
                  )}
                  {e.sbt_response_draft && (
                    <div className="bg-cyan-50 border border-cyan-200 rounded-md p-3">
                      <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-semibold mb-1 flex items-center gap-1">
                        <Bell className="w-3 h-3" /> Suggested SBT response
                      </div>
                      <div className="text-sm text-slate-800 leading-relaxed">{e.sbt_response_draft}</div>
                    </div>
                  )}

                  {/* Before/After diff */}
                  {(e.before?.title || e.before?.h1 || e.before?.description) && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 font-semibold uppercase tracking-wider">Before / after diff</summary>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="bg-red-50 border border-red-100 rounded-md p-2">
                          <div className="text-[10px] text-red-600 font-semibold uppercase">Before</div>
                          {e.before?.title && <div className="mt-1"><span className="text-slate-500">Title:</span> {e.before.title}</div>}
                          {e.before?.h1 && <div className="mt-1"><span className="text-slate-500">H1:</span> {e.before.h1}</div>}
                          {e.before?.description && <div className="mt-1"><span className="text-slate-500">Desc:</span> {e.before.description}</div>}
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 rounded-md p-2">
                          <div className="text-[10px] text-emerald-700 font-semibold uppercase">After</div>
                          {e.after?.title && <div className="mt-1"><span className="text-slate-500">Title:</span> {e.after.title}</div>}
                          {e.after?.h1 && <div className="mt-1"><span className="text-slate-500">H1:</span> {e.after.h1}</div>}
                          {e.after?.description && <div className="mt-1"><span className="text-slate-500">Desc:</span> {e.after.description}</div>}
                        </div>
                      </div>
                    </details>
                  )}

                  {/* New blog posts */}
                  {(e.new_blog_posts || []).length > 0 && (
                    <div className="text-xs">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">New content published</div>
                      <ul className="space-y-1">
                        {e.new_blog_posts.map((p, i) => (
                          <li key={i}>
                            <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline inline-flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" /> {p.title || p.url}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ── COMPETITOR LIST ── */}
        <TabsContent value="list" className="mt-4 space-y-3">
          {competitors.map((c) => (
            <Card key={c.id} data-testid={`comp-${c.id}`}>
              <CardContent className="pt-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">
                      <a href={c.homepage_url} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-600 inline-flex items-center gap-1">
                        {c.homepage_url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    {c.blog_url && (
                      <div className="text-xs text-slate-400">
                        Blog: <a href={c.blog_url} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-600">{c.blog_url}</a>
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1">
                      Last crawled: {fmt(c.last_crawled_at)}
                      {c.last_error && <span className="text-red-500 ml-2">· {c.last_error}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-slate-600 flex items-center gap-2 cursor-pointer">
                      <Switch checked={!!c.active} onCheckedChange={() => toggleActive(c)} data-testid={`comp-active-${c.id}`} />
                      Active
                    </label>
                    <Button variant="outline" size="sm" onClick={() => crawlOne(c.id)} data-testid={`comp-crawl-${c.id}`}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Crawl
                    </Button>
                    <button onClick={() => removeCompetitor(c.id)} className="text-slate-400 hover:text-red-600" data-testid={`comp-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ── ADD ── */}
        <TabsContent value="add" className="mt-4">
          <AddCompetitor
            onAdded={async () => { await load(); toast.success("Competitor added"); }}
            hdr={hdr}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AddCompetitor({ onAdded, hdr }) {
  const [f, setF] = useState({ name: "", homepage_url: "", blog_url: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name || !f.homepage_url) return toast.error("Name + homepage URL required");
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/sbt/competitors`, { method: "POST", headers: hdr(), body: JSON.stringify(f) });
      setF({ name: "", homepage_url: "", blog_url: "" });
      await onAdded();
    } catch { toast.error("Add failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Add a competitor to monitor</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div><Label className="text-xs">Company name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-comp-name" /></div>
        <div><Label className="text-xs">Homepage URL</Label><Input value={f.homepage_url} onChange={(e) => setF({ ...f, homepage_url: e.target.value })} placeholder="https://example.com" data-testid="add-comp-homepage" /></div>
        <div><Label className="text-xs">Blog URL <span className="text-slate-400">(optional — improves new-content detection)</span></Label><Input value={f.blog_url} onChange={(e) => setF({ ...f, blog_url: e.target.value })} placeholder="https://example.com/blog" data-testid="add-comp-blog" /></div>
        <Button onClick={submit} disabled={saving} data-testid="add-comp-submit">
          <Plus className="w-4 h-4 mr-1" /> {saving ? "Adding…" : "Add competitor"}
        </Button>
      </CardContent>
    </Card>
  );
}
